import React from 'react';
import { useController, useWatch } from 'react-hook-form';
import {
  FieldType,
  FilterType,
  IFieldFieldsFragment,
  INamedFilterFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';
import useFilterWidgetFormContext from '../../../utils/useFilterWidgetFormContext';
import {
  castToFieldFragment,
  castToOptionalFieldFragment,
  defaultPredicate,
  FilterWidgetFormFieldPathType,
  IFilterPredicate,
  IPredicateFormFieldPath,
} from '../../../types';
import FilterWidgetFieldEntity from './FilterWidgetFieldEntity';
import isNil from 'lodash/isNil';
import { useFilterWidgetPropContext } from '../../../FilterWidgetPropContext';
import { useResetPredicates } from '../../../utils/useResetPredicates';
import { getPredicateFormFieldPath } from '../../../utils/formPath';
import { UnreachableCaseError } from 'ts-essentials';
import { unstable_batchedUpdates } from 'react-dom';

interface IProps {
  formPath: IPredicateFormFieldPath;
  onReset: () => void;
  rootPredicate: IFilterPredicate;
  onUpdateRootPredicate: (newPredicate: IFilterPredicate) => void;
}

const shouldSwitchToArrayObjectPredicate = (
  rootPredicate: IFilterPredicate,
  newValue: IFieldFieldsFragment,
) => {
  if (newValue.nestedFieldMetadata?.type === FieldType.ArrayObject) {
    if (rootPredicate.__typename === 'SimplePredicate') {
      return true;
    } else if (rootPredicate.__typename === 'ArrayObjectPredicate') {
      // if existing predicate elements are for different nested field,
      // also reset the predicate element
      for (const predicateElement of rootPredicate.predicateElements) {
        const oldValue = predicateElement.field;
        if (!isNil(oldValue)) {
          const oldValueField = castToFieldFragment(oldValue);
          if (
            !isNil(oldValueField.nestedFieldMetadata) &&
            oldValueField.nestedFieldMetadata.fieldKey !==
              newValue.nestedFieldMetadata.fieldKey
          ) {
            return true;
          }
        }
      }
    } else {
      throw new UnreachableCaseError(rootPredicate);
    }
  }

  return false;
};

const shouldSwitchToSimplePredicate = (
  rootPredicate: IFilterPredicate,
  newValue: IFieldFieldsFragment,
) =>
  rootPredicate.__typename === 'ArrayObjectPredicate' &&
  newValue.nestedFieldMetadata?.type !== FieldType.ArrayObject;

const getRootFieldMetadata = (rootPredicate: IFilterPredicate) => {
  if (rootPredicate.__typename === 'ArrayObjectPredicate') {
    for (const { field } of rootPredicate.predicateElements) {
      if (!isNil(field) && !isNil(field.nestedFieldMetadata)) {
        return castToFieldFragment(field).nestedFieldMetadata;
      }
    }
  }

  return null;
};

const PredicateField = ({
  formPath,
  onReset,
  rootPredicate,
  onUpdateRootPredicate,
}: IProps) => {
  const { lockedField, disableNamedFilters } = useFilterWidgetPropContext();

  const fieldPath = getPredicateFormFieldPath(formPath);

  const { reset, control, setValue, setFocus } = useFilterWidgetFormContext();

  const { field, fieldState } = useController({
    name: `${fieldPath}.field`,
    control,
  });

  const selectedOperandType = useWatch({
    name: `${fieldPath}.operand.type`,
    control,
  });

  const resetPredicates = useResetPredicates({ formPath });

  const rootFieldMetadata = getRootFieldMetadata(rootPredicate);

  const handleChange = (
    newValue: IFieldFieldsFragment | INamedFilterFieldsFragment | null,
  ) => {
    if (!newValue) {
      onReset();
      return;
    }

    // User is allowed to select a Named filter in first predicate of the first
    // condition. In this case we will reset form to a "named" type and show
    // a different UI
    if (newValue.__typename === 'NamedFilter') {
      // (KB-8133) Resetting the form is not atomic (?!) - RHF seems to update `type` first, then React rerenders,
      // then `namedFilter`, then again React rerenders. The inconsistent invariant (type=Named, and no namedFilter)
      // causes other components to fail which assume that if type=Named, then there is namedFilter.
      // Wrapping the function in unstable_batchedUpdates causes React to update render atomically.
      // React 18 does this automatically, and should be removed during upgraded
      // See: https://github.com/reactwg/react-18/discussions/21
      unstable_batchedUpdates(() => {
        reset({
          type: FilterType.Named,
          namedFilter: newValue,
        });
      });
    } else {
      if (shouldSwitchToArrayObjectPredicate(rootPredicate, newValue)) {
        const arrayObjectPredicate: IFilterPredicate = {
          __typename: 'ArrayObjectPredicate',
          predicateElements: [
            {
              ...defaultPredicate,
              field: newValue,
            },
          ],
        };
        onUpdateRootPredicate(arrayObjectPredicate);
      } else if (shouldSwitchToSimplePredicate(rootPredicate, newValue)) {
        const simplePredicate: IFilterPredicate = {
          ...defaultPredicate,
          field: newValue,
        };
        onUpdateRootPredicate(simplePredicate);
      } else {
        field.onChange(newValue);

        // Because relOp is an object - resetField will not work for it therefore
        // we use setValue
        setValue(`${fieldPath}.relOp`, null);

        resetPredicates(selectedOperandType);

        // Focusing next field (Operator) with setFocus
        setFocus(`${fieldPath}.relOp`);
      }
    }
  };

  const namedFiltersAllowed =
    !disableNamedFilters &&
    formPath.conditionIndex === 0 &&
    formPath.predicateIndex === 0 &&
    (formPath.type === FilterWidgetFormFieldPathType.SimplePredicate ||
      (formPath.type === FilterWidgetFormFieldPathType.ArrayObjectPredicate &&
        formPath.predicateElementIndex === 0));

  const placeholder = !isNil(rootFieldMetadata)
    ? `Select a property of "${rootFieldMetadata.name}" field`
    : namedFiltersAllowed
      ? 'Select field or existing filter'
      : 'Select field';

  return (
    <FilterWidgetFieldEntity
      field={castToOptionalFieldFragment(field.value)}
      onChange={handleChange}
      placeholder={placeholder}
      allowNamedFilter={namedFiltersAllowed}
      allowField={!lockedField}
      operandTypeIsField={false}
      autoFocus={!field.value}
      hasError={Boolean(fieldState.error)}
      isReadOnly={!isNil(lockedField)}
      rootFieldKey={rootFieldMetadata?.fieldKey}
    />
  );
};

export default PredicateField;
